/**
 * ================================================================
 * TYPE DEFINITIONS: Toast Components & State
 * ================================================================
 *
 * @fileOverview     Type definitions for toast notification system
 * @module           types/ui/toast
 * @version          1.0.0
 * @since            2025-09-10
 * @author           Development Team <dev@company.com>
 *
 * @purpose          Centralized type definitions for toast components and state management
 * @exports          All interfaces and types related to toast functionality
 *
 * @organization
 * - Component types: UI component prop interfaces
 * - State types: Toast state management interfaces
 * - Action types: Redux-style action definitions
 * - Utility types: Helper types for toast operations
 *
 * @usage
 * ```typescript
 * import type { ToastProps, ToasterToast, ToastState } from '@/types/ui/toast';
 * ```
 *
 * ================================================================
 */

import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';

// ================================================================
// TOAST COMPONENT TYPES
// ================================================================

/**
 * Toast action element type for interactive toast buttons
 */
export type ToastActionElement = React.ReactElement<typeof ToastPrimitives.Action>;

/**
 * Base toast component props extending Radix UI Toast Root props
 */
export interface ToastProps extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> {
  className?: string;
  variant?: 'default' | 'destructive' | 'success';
}

/**
 * Toast provider component props
 */
export interface ToastProviderProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Provider> {
  children?: React.ReactNode;
}

/**
 * Toast viewport component props
 */
export interface ToastViewportProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport> {
  className?: string;
}

/**
 * Toast action component props
 */
export interface ToastActionProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action> {
  className?: string;
}

/**
 * Toast close button component props
 */
export interface ToastCloseProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close> {
  className?: string;
}

/**
 * Toast title component props
 */
export interface ToastTitleProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title> {
  className?: string;
}

/**
 * Toast description component props
 */
export interface ToastDescriptionProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description> {
  className?: string;
}

// ================================================================
// TOAST STATE MANAGEMENT TYPES
// ================================================================

/**
 * Enhanced toast type with additional properties for state management
 */
export interface ToasterToast extends Omit<ToastProps, 'title'> {
  action?: ToastActionElement;
  description?: React.ReactNode;
  id: string;
  title?: React.ReactNode;
}

/**
 * Toast input type for creating new toasts (without ID)
 */
export type ToastInput = Omit<ToasterToast, 'id'>;

/**
 * Toast state interface for managing active toasts
 */
export interface ToastState {
  toasts: ToasterToast[];
}

// ================================================================
// TOAST ACTION TYPES
// ================================================================

/**
 * Available toast action types for state management
 */
export const TOAST_ACTION_TYPES = {
  ADD_TOAST: 'ADD_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
} as const;

export type ToastActionType = typeof TOAST_ACTION_TYPES;

/**
 * Add toast action
 */
export interface AddToastAction {
  toast: ToasterToast;
  type: ToastActionType['ADD_TOAST'];
}

/**
 * Update toast action
 */
export interface UpdateToastAction {
  toast: Partial<ToasterToast>;
  type: ToastActionType['UPDATE_TOAST'];
}

/**
 * Dismiss toast action
 */
export interface DismissToastAction {
  toastId?: ToasterToast['id'];
  type: ToastActionType['DISMISS_TOAST'];
}

/**
 * Remove toast action
 */
export interface RemoveToastAction {
  toastId?: ToasterToast['id'];
  type: ToastActionType['REMOVE_TOAST'];
}

/**
 * Union type for all toast actions
 */
export type ToastAction =
  | AddToastAction
  | UpdateToastAction
  | DismissToastAction
  | RemoveToastAction;

// ================================================================
// TOAST HOOK TYPES
// ================================================================

/**
 * Return type for useToast hook
 */
export interface UseToastReturn extends ToastState {
  dismiss: (toastId?: string) => void;
  toast: (props: ToastInput) => {
    dismiss: () => void;
    id: string;
    update: (props: Partial<ToasterToast>) => void;
  };
}

// ================================================================
// TOAST CONFIGURATION TYPES
// ================================================================

/**
 * Toast configuration constants
 */
export interface ToastConfig {
  TOAST_LIMIT: number;
  TOAST_REMOVE_DELAY: number;
}

/**
 * Toast variant options
 */
export type ToastVariant = 'default' | 'destructive' | 'success';

// ================================================================
// MAIN TOAST COMPONENT TYPES (formerly GlobalToast)
// ================================================================

/**
 * Props for the main Toast component (replaces GlobalToastProps)
 */
export interface ToastComponentProps {
  /** Auto-close duration in milliseconds */
  autoClose?: number;
  /** Toast description text */
  description?: string;
  /** Controls toast visibility */
  open?: boolean;
  /** Toast title text */
  title?: string;
  /** Toast variant - 'success', 'destructive', or 'default' */
  variant?: ToastVariant;
}

// ================================================================
// TOAST UTILITY TYPES
// ================================================================

/**
 * Toast timeout map type for managing toast removal timeouts
 */
export type ToastTimeoutMap = Map<string, ReturnType<typeof setTimeout>>;

/**
 * Toast listener function type
 */
export type ToastListener = (state: ToastState) => void;

/**
 * ================================================================
 * END OF TYPES: Toast Components & State
 * ================================================================
 *
 * @exports          All toast-related types and interfaces
 * @dependencies     React, Radix UI Toast primitives, class-variance-authority
 * @usedBy           Toast components, useToast hook, toast state management
 *
 * @maintenance
 * - Keep interfaces sorted alphabetically
 * - Update when toast functionality changes
 * - Ensure properties remain sorted A-Z
 *
 * ================================================================
 */

// This is a placeholder to satisfy the import in ToastProps
// The actual toastVariants will be defined in the toast.tsx component
declare const toastVariants: any;
